import { lazy, useEffect, type ComponentType } from 'react';
import type { RectDeTarjeta } from './coreografia-de-apertura';
import type { LogoDePrixia } from './elegir-logo';
import type { Momento } from './momentos';
import type { EstadoDeApertura } from './tiempos-de-apertura';

export interface PropsDeEscena {
  logo: LogoDePrixia;
  medirTarjetas: (lienzo: HTMLCanvasElement) => RectDeTarjeta[];
  alCambiar: (estado: EstadoDeApertura) => void;
  alListo: () => void;
  alFallar: () => void;
}

export interface PropsDeEscenaDeArmando {
  logo: LogoDePrixia;
  /** Los pasos reales ya cumplidos, de 0 a 4. */
  hechos: number;
  alListo: () => void;
  /** Una sola vez, cuando el logo llegó junto a «Prixia». */
  alTerminar: () => void;
  alFallar: () => void;
}

export interface PropsDeEscenaDeMision {
  logo: LogoDePrixia;
  /** Cuántas misiones hay hechas, contando la que se acaba de cumplir. */
  enTotal: number;
  fondo: 'claro' | 'oscuro';
  alListo: () => void;
  /** Una sola vez, cuando la pieza encajó y pasó la pausa. */
  alTerminar: () => void;
  alFallar: () => void;
}

export interface PropsDeEscenaDeMomento {
  momento: Momento;
  logo: LogoDePrixia;
  fondo: 'claro' | 'oscuro';
  alListo: () => void;
  /** Una sola vez, cuando el momento terminó. */
  alTerminar: () => void;
  alFallar: () => void;
}

/** Si el módulo no baja no hay escena: se avisa y la pantalla sigue sin ella. */
function SinEscena({ alFallar }: Pick<PropsDeEscena, 'alFallar'>) {
  useEffect(() => {
    alFallar();
  }, [alFallar]);
  return null;
}

let pedido: Promise<{ default: ComponentType<PropsDeEscena> }> | null = null;

/** Baja la escena una sola vez; `EscenaDiferida` usa este mismo pedido. */
export const precargarEscena = () =>
  (pedido ??= import('./escena-de-apertura').catch(() => ({ default: SinEscena })));

export const EscenaDiferida = lazy(precargarEscena);

let pedidoDeArmando: Promise<{ default: ComponentType<PropsDeEscenaDeArmando> }> | null = null;

/** Baja la escena de «Armando» una sola vez; `EscenaDeArmandoDiferida` usa este mismo pedido. */
export const precargarEscenaDeArmando = () =>
  (pedidoDeArmando ??= import('./escena-de-armando').catch(() => ({ default: SinEscena })));

export const EscenaDeArmandoDiferida = lazy(precargarEscenaDeArmando);

let pedidoDeMision: Promise<{ default: ComponentType<PropsDeEscenaDeMision> }> | null = null;

/** La tarjeta de misiones lo precarga en un rato libre, sólo en una demo; el panel lo usa por el mismo pedido. */
export const cargarEscenaDeMision = () =>
  (pedidoDeMision ??= import('./escena-de-mision').catch(() => ({ default: SinEscena })));

export const EscenaDeMisionDiferida = lazy(cargarEscenaDeMision);

let pedidoDeMomento: Promise<{ default: ComponentType<PropsDeEscenaDeMomento> }> | null = null;

/** Baja la escena del primer ingreso y del plan completo una sola vez. */
export const cargarEscenaDeMomento = () =>
  (pedidoDeMomento ??= import('./escena-de-momento').catch(() => ({ default: SinEscena })));

export const EscenaDeMomentoDiferida = lazy(cargarEscenaDeMomento);
